import React from 'react';
import { CaseProfilePayload, postureLabel, modelLabel, formatSetAt } from './caseProfile';

export interface CaseTypeChipProps {
  profile: CaseProfilePayload | null | undefined;
  className?: string;
}

export function CaseTypeChip({ profile, className }: CaseTypeChipProps) {
  if (!profile || profile.schema_version !== '1.0') {
    return null;
  }

  const pLabel = postureLabel(profile.posture);
  const mLabel = modelLabel(profile.damages_model);
  const baseLabel = `${pLabel} · ${mLabel}`;

  const isNullPosture = profile.posture === null || profile.posture === undefined;
  const isConfirmed = !isNullPosture && profile.posture_confirmed === true;

  let borderColor: string;
  let bgColor: string;
  let textColor: string;
  let dotColor: string;
  let title: string;
  let ariaState: string;
  let label: string;
  let suffix: React.ReactNode = null;

  if (isNullPosture) {
    borderColor = 'var(--color-border-sub)';
    bgColor = 'transparent';
    textColor = 'var(--color-text-dim)';
    dotColor = 'var(--color-text-ghost)';
    label = baseLabel;
    suffix = <span style={{ color: 'var(--color-text-ghost)' }}> –</span>;
    title = 'No posture on record — first-party cap applied on assumption.';
    ariaState = 'not set';
  } else if (isConfirmed) {
    borderColor = 'var(--color-verified)';
    bgColor = 'transparent';
    textColor = 'var(--color-text-secondary)';
    dotColor = 'var(--color-verified)';
    label = baseLabel;
    suffix = <span style={{ color: 'var(--color-verified)' }}> ✓</span>;
    const by = profile.set_by ? ` by ${profile.set_by}` : '';
    const when = profile.set_at ? ` · ${formatSetAt(profile.set_at)}` : '';
    title = `Posture confirmed${by}${when}.`;
    ariaState = 'confirmed';
  } else {
    borderColor = 'var(--color-needs-human-tint-bd)';
    bgColor = 'var(--color-needs-human-tint)';
    textColor = 'var(--color-needs-human-text)';
    dotColor = 'var(--color-needs-human)';
    label = `${baseLabel} · INFERRED`;
    title = 'Posture inferred — not yet confirmed. The first-party cap is applied on assumption.';
    ariaState = 'inferred — not yet confirmed';
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] border text-[10px] font-mono uppercase tracking-[0.08em] whitespace-nowrap select-none cursor-default ${className || ''}`}
      style={{
        borderColor,
        backgroundColor: bgColor,
        color: textColor,
      }}
      title={title}
      aria-label={`Case posture ${pLabel}, damages model ${mLabel}, ${ariaState}.`}
      data-testid="case-type-chip"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor }} />
      <span>{label}</span>
      {suffix}
    </span>
  );
}

export default CaseTypeChip;
